/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useEffect, useState } from 'react'
import { useParams, Link, useHistory } from 'react-router-dom'
import { AppContext } from '../context/AppContext'
import PaymentReceiveService from '../services/PaymentReceiveService'
import Swal from 'sweetalert2'

const PaymentReceiveDetails = () => {
    const { currentUser } = useContext(AppContext)
    const { paymentId } = useParams()
    const history = useHistory()
    const [payment, setPayment] = useState(null)
    const [loading, setLoading] = useState(true)
    const [updating, setUpdating] = useState(false)

    useEffect(() => {
        fetchPaymentDetails()
    }, [paymentId])

    const fetchPaymentDetails = async () => {
        setLoading(true)
        try {
            const response = await PaymentReceiveService.getPaymentReceiveDetails(paymentId)
            if (response.success) {
                setPayment(response.data)
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.errorMessage || 'Failed to fetch payment details',
                    confirmButtonColor: '#d33'
                })
                history.push('/payments/receive')
            }
        } catch (error) {
            console.error('Error fetching payment details:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error fetching payment details',
                confirmButtonColor: '#d33'
            })
            history.push('/payments/receive')
        } finally {
            setLoading(false)
        }
    }

    const handleStatusUpdate = async (newStatus) => {
        const statusText = newStatus === 'approved' ? 'approve' : 'reject'
        const result = await Swal.fire({
            title: `Are you sure?`,
            text: `Do you want to ${statusText} this payment?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: newStatus === 'approved' ? '#28a745' : '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: `Yes, ${statusText} it!`
        })

        if (result.isConfirmed) {
            setUpdating(true)
            try {
                const response = await PaymentReceiveService.updatePaymentStatus(paymentId, { status: newStatus })
                if (response.success) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Updated!',
                        text: `Payment has been ${statusText}d successfully.`,
                        confirmButtonColor: '#3085d6',
                        timer: 2000,
                        showConfirmButton: false
                    })
                    fetchPaymentDetails()
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: response.errorMessage || `Failed to ${statusText} payment`,
                        confirmButtonColor: '#d33'
                    })
                }
            } catch (error) {
                console.error('Error updating payment status:', error)
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: `Error ${statusText}ing payment`,
                    confirmButtonColor: '#d33'
                })
            } finally {
                setUpdating(false)
            }
        }
    }

    const handleDeletePayment = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        })

        if (result.isConfirmed) {
            setUpdating(true)
            try {
                const response = await PaymentReceiveService.deletePaymentReceive(paymentId)
                if (response.success) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Payment has been deleted successfully.',
                        confirmButtonColor: '#3085d6',
                        timer: 2000,
                        showConfirmButton: false
                    })
                    history.push('/payments/receive')
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: response.errorMessage || 'Failed to delete payment',
                        confirmButtonColor: '#d33'
                    })
                }
            } catch (error) {
                console.error('Error deleting payment:', error)
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error deleting payment',
                    confirmButtonColor: '#d33'
                })
            } finally {
                setUpdating(false)
            }
        }
    }

    const downloadFile = () => {
        if (payment.files) {
            PaymentReceiveService.downloadPaymentFile(payment.files, payment.originalFileName)
        }
    }

    const getStatusBadge = (status) => {
        const statusInfo = PaymentReceiveService.formatPaymentStatus(status)
        return `badge-${statusInfo.color}`
    }

    const formatDate = (dateString) => {
        return PaymentReceiveService.formatDate(dateString)
    }

    if (loading) {
        return (
            <div className='card card-custom'>
                <div className='card-body text-center py-5'>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading payment details...</p>
                </div>
            </div>
        )
    }

    if (!payment) {
        return (
            <div className='card card-custom'>
                <div className='card-body text-center py-5'>
                    <div className='text-muted'>
                        <i className='fas fa-exclamation-triangle fa-3x mb-3'></i>
                        <p>Payment not found</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            <div className='card card-custom'>
                <div className='card-header card-header-mobile'>
                    <div className='card-title'>
                        <h3 className='card-label'>Payment Details</h3>
                        <div className='card-label-mobile'>
                            <span className={`badge ${getStatusBadge(payment.status)} fs-6`}>
                                {PaymentReceiveService.formatPaymentStatus(payment.status).label}
                            </span>
                        </div>
                    </div>
                    <div className='card-toolbar card-toolbar-mobile'>
                        <Link to="/payments/receive" className='btn btn-secondary btn-mobile'>
                            <i className="fas fa-arrow-left me-2"></i>
                            <span className="btn-text">Back to List</span>
                        </Link>
                        <Link to={`/payments/receive/edit/${payment.id}`} className='btn btn-warning btn-mobile'>
                            <i className="fas fa-edit me-2"></i>
                            <span className="btn-text">Edit</span>
                        </Link>
                        {payment.status === 'created' && (
                            <button
                                className='btn btn-success btn-mobile'
                                onClick={() => handleStatusUpdate('approved')}
                                disabled={updating}
                            >
                                {updating ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        <span className="btn-text">Updating...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-check me-2"></i>
                                        <span className="btn-text">Approve</span>
                                    </>
                                )}
                            </button>
                        )}
                        <button
                            className='btn btn-danger btn-mobile'
                            onClick={handleDeletePayment}
                            disabled={updating}
                        >
                            {updating ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    <span className="btn-text">Deleting...</span>
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-trash me-2"></i>
                                    <span className="btn-text">Delete</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>

                <div className='card-body'>
                    <div className='row'>
                        {/* Payment Information */}
                        <div className='col-lg-8'>
                            <div className='payment-info-section'>
                                <h4 className='section-title'>
                                    <i className='fas fa-money-bill-wave me-2 text-primary'></i>
                                    Payment Information
                                </h4>
                                
                                <div className='row'>
                                    <div className='col-md-6'>
                                        <div className='info-item'>
                                            <label className='info-label'>Reference Number</label>
                                            <div className='info-value'>
                                                <span className='badge bg-primary fs-6'>{payment.reference}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className='info-item'>
                                            <label className='info-label'>Payment Status</label>
                                            <div className='info-value'>
                                                <span className={`badge ${getStatusBadge(payment.status)} fs-6`}>
                                                    {PaymentReceiveService.formatPaymentStatus(payment.status).label}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className='row'>
                                    <div className='col-md-6'>
                                        <div className='info-item'>
                                            <label className='info-label'>Amount</label>
                                            <div className='info-value'>
                                                <span className='fw-bold text-success fs-5'>
                                                    {PaymentReceiveService.formatCurrency(payment.amount)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className='info-item'>
                                            <label className='info-label'>Payment Type</label>
                                            <div className='info-value'>
                                                <span className='badge bg-info fs-6'>{payment.paymentType}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className='row'>
                                    <div className='col-md-6'>
                                        <div className='info-item'>
                                            <label className='info-label'>Payment Category</label>
                                            <div className='info-value'>
                                                <span className='text-muted'>
                                                    {payment.paymentCategory || 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className='info-item'>
                                            <label className='info-label'>Transaction Mode</label>
                                            <div className='info-value'>
                                                <span className='text-muted'>
                                                    {payment.modeOfTransaction || 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className='row'>
                                    <div className='col-md-6'>
                                        <div className='info-item'>
                                            <label className='info-label'>Transaction ID</label>
                                            <div className='info-value'>
                                                <span className='text-muted'>
                                                    {payment.transactionId || 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className='info-item'>
                                            <label className='info-label'>Created Date</label>
                                            <div className='info-value'>
                                                <span className='text-muted'>
                                                    {formatDate(payment.createdAt)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {payment.notes && (
                                    <div className='row'>
                                        <div className='col-12'>
                                            <div className='info-item'>
                                                <label className='info-label'>Notes</label>
                                                <div className='info-value'>
                                                    <div className='alert alert-info mb-0'>
                                                        {payment.notes}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {payment.files && (
                                    <div className='row'>
                                        <div className='col-12'>
                                            <div className='info-item'>
                                                <label className='info-label'>Attached File</label>
                                                <div className='info-value'>
                                                    <button
                                                        className='btn btn-outline-primary btn-sm'
                                                        onClick={downloadFile}
                                                    >
                                                        <i className='fas fa-download me-2'></i>
                                                        Download {payment.originalFileName}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Order & Customer Information */}
                        <div className='col-lg-4'>
                            <div className='order-info-section'>
                                <h4 className='section-title'>
                                    <i className='fas fa-shopping-cart me-2 text-success'></i>
                                    Order Information
                                </h4>
                                
                                <div className='info-item'>
                                    <label className='info-label'>Order Number</label>
                                    <div className='info-value'>
                                        <Link 
                                            to={`/sales-orders/${payment.orderId}`}
                                            className='text-decoration-none fw-bold text-primary'
                                        >
                                            {payment.orderNo}
                                        </Link>
                                    </div>
                                </div>

                                <div className='info-item'>
                                    <label className='info-label'>Order Status</label>
                                    <div className='info-value'>
                                        <span className='badge bg-secondary'>{payment.orderStatus}</span>
                                    </div>
                                </div>

                                <div className='info-item'>
                                    <label className='info-label'>Order Total</label>
                                    <div className='info-value'>
                                        <span className='fw-bold text-dark'>
                                            {PaymentReceiveService.formatCurrency(payment.grandTotal)}
                                        </span>
                                    </div>
                                </div>

                                <hr className='my-4' />

                                <h4 className='section-title'>
                                    <i className='fas fa-user me-2 text-info'></i>
                                    Customer Information
                                </h4>
                                
                                <div className='info-item'>
                                    <label className='info-label'>Customer Name</label>
                                    <div className='info-value'>
                                        <span className='fw-bold'>{payment.customerName}</span>
                                    </div>
                                </div>

                                <div className='info-item'>
                                    <label className='info-label'>Email</label>
                                    <div className='info-value'>
                                        <span className='text-muted'>
                                            {payment.customerEmail || 'N/A'}
                                        </span>
                                    </div>
                                </div>

                                <div className='info-item'>
                                    <label className='info-label'>Mobile Number</label>
                                    <div className='info-value'>
                                        <span className='text-muted'>
                                            {payment.mobileNumber || 'N/A'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment Actions */}
                    <div className='row mt-4'>
                        <div className='col-12'>
                            <div className='payment-actions-section'>
                                <h4 className='section-title'>
                                    <i className='fas fa-cogs me-2 text-warning'></i>
                                    Payment Actions
                                </h4>
                                
                                <div className='row'>
                                    <div className='col-md-6'>
                                        <div className='action-card'>
                                            <div className='action-card-header'>
                                                <i className='fas fa-print text-primary'></i>
                                                <h5>Print Receipt</h5>
                                            </div>
                                            <div className='action-card-body'>
                                                <p className='text-muted'>Generate and print payment receipt</p>
                                                <button className='btn btn-outline-primary btn-sm'>
                                                    <i className='fas fa-print me-2'></i>
                                                    Print Receipt
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className='col-md-6'>
                                        <div className='action-card'>
                                            <div className='action-card-header'>
                                                <i className='fas fa-envelope text-success'></i>
                                                <h5>Send Receipt</h5>
                                            </div>
                                            <div className='action-card-body'>
                                                <p className='text-muted'>Send payment receipt via email</p>
                                                <button className='btn btn-outline-success btn-sm'>
                                                    <i className='fas fa-envelope me-2'></i>
                                                    Send Email
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                /* Card Header */
                .card-header-mobile {
                    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
                    border-bottom: 1px solid #e9ecef;
                    border-radius: 0.75rem 0.75rem 0 0 !important;
                    padding: 1.5rem;
                    position: relative;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }

                .card-title {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    margin: 0;
                }

                .card-label {
                    color: #212529;
                    font-weight: 600;
                    margin: 0;
                    font-size: 1.5rem;
                }

                .card-label-mobile {
                    display: flex;
                    align-items: center;
                }

                .card-toolbar-mobile {
                    display: flex;
                    gap: 0.75rem;
                    align-items: center;
                    flex-wrap: wrap;
                }

                .btn-mobile {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    border-radius: 0.5rem;
                    font-weight: 500;
                    font-size: 0.875rem;
                    padding: 0.625rem 1.25rem;
                    border: none;
                    transition: all 0.3s ease;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                }

                .btn-mobile:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
                }

                .btn-mobile i {
                    font-size: 0.875rem;
                }

                /* Section Styling */
                .section-title {
                    color: #212529;
                    font-weight: 600;
                    margin-bottom: 1.5rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 2px solid #e9ecef;
                    font-size: 1.25rem;
                }

                .payment-info-section,
                .order-info-section,
                .payment-actions-section {
                    background: white;
                    border-radius: 0.75rem;
                    padding: 1.5rem;
                    border: 1px solid #e9ecef;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                    margin-bottom: 1.5rem;
                }

                /* Info Items */
                .info-item {
                    margin-bottom: 1.25rem;
                }

                .info-item:last-child {
                    margin-bottom: 0;
                }

                .info-label {
                    display: block;
                    font-weight: 600;
                    color: #495057;
                    margin-bottom: 0.5rem;
                    font-size: 0.875rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .info-value {
                    font-size: 1rem;
                    color: #212529;
                }

                .info-value .badge {
                    font-size: 0.875rem;
                    padding: 0.5rem 1rem;
                }

                /* Action Cards */
                .action-card {
                    background: #f8f9fa;
                    border-radius: 0.75rem;
                    padding: 1.25rem;
                    border: 1px solid #e9ecef;
                    transition: all 0.3s ease;
                    height: 100%;
                }

                .action-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    border-color: #007bff;
                }

                .action-card-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 1rem;
                }

                .action-card-header i {
                    font-size: 1.5rem;
                }

                .action-card-header h5 {
                    margin: 0;
                    font-weight: 600;
                    color: #212529;
                    font-size: 1.1rem;
                }

                .action-card-body p {
                    margin-bottom: 1rem;
                    font-size: 0.875rem;
                }

                /* Responsive Design */
                @media (max-width: 991.98px) {
                    .card-header-mobile {
                        padding: 1rem;
                        flex-direction: column;
                        align-items: stretch;
                        gap: 1rem;
                    }

                    .card-title {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.5rem;
                    }

                    .card-label {
                        font-size: 1.25rem;
                    }

                    .card-toolbar-mobile {
                        width: 100%;
                        justify-content: stretch;
                        gap: 0.5rem;
                    }

                    .btn-mobile {
                        flex: 1;
                        justify-content: center;
                        padding: 0.75rem 1rem;
                        font-size: 0.875rem;
                        min-height: 2.5rem;
                    }

                    .payment-info-section,
                    .order-info-section,
                    .payment-actions-section {
                        padding: 1rem;
                        margin-bottom: 1rem;
                    }

                    .section-title {
                        font-size: 1.1rem;
                        margin-bottom: 1rem;
                    }

                    .info-item {
                        margin-bottom: 1rem;
                    }

                    .action-card {
                        padding: 1rem;
                        margin-bottom: 1rem;
                    }

                    .action-card-header {
                        margin-bottom: 0.75rem;
                    }

                    .action-card-header h5 {
                        font-size: 1rem;
                    }
                }

                @media (max-width: 767.98px) {
                    .card-header-mobile {
                        padding: 0.75rem;
                        border-radius: 0.5rem 0.5rem 0 0 !important;
                    }

                    .card-label {
                        font-size: 1.1rem;
                        text-align: center;
                    }

                    .btn-mobile {
                        padding: 0.625rem 0.75rem;
                        font-size: 0.8rem;
                        min-height: 2.25rem;
                    }

                    .btn-mobile .btn-text {
                        font-size: 0.8rem;
                    }

                    .payment-info-section,
                    .order-info-section,
                    .payment-actions-section {
                        padding: 0.75rem;
                        border-radius: 0.5rem;
                    }

                    .section-title {
                        font-size: 1rem;
                        margin-bottom: 0.75rem;
                        padding-bottom: 0.5rem;
                    }

                    .info-label {
                        font-size: 0.8rem;
                    }

                    .info-value {
                        font-size: 0.9rem;
                    }

                    .info-value .badge {
                        font-size: 0.8rem;
                        padding: 0.375rem 0.75rem;
                    }

                    .action-card {
                        padding: 0.75rem;
                    }

                    .action-card-header {
                        margin-bottom: 0.5rem;
                    }

                    .action-card-header i {
                        font-size: 1.25rem;
                    }

                    .action-card-header h5 {
                        font-size: 0.9rem;
                    }

                    .action-card-body p {
                        font-size: 0.8rem;
                        margin-bottom: 0.75rem;
                    }
                }

                @media (max-width: 575.98px) {
                    .card-header-mobile {
                        padding: 0.5rem;
                    }

                    .card-title {
                        gap: 0.25rem;
                    }

                    .card-label {
                        font-size: 1rem;
                    }

                    .btn-mobile {
                        padding: 0.5rem 0.625rem;
                        font-size: 0.75rem;
                        min-height: 2rem;
                    }

                    .btn-mobile i {
                        font-size: 0.75rem;
                    }

                    .payment-info-section,
                    .order-info-section,
                    .payment-actions-section {
                        padding: 0.5rem;
                    }

                    .section-title {
                        font-size: 0.9rem;
                        margin-bottom: 0.5rem;
                        padding-bottom: 0.375rem;
                    }

                    .info-item {
                        margin-bottom: 0.75rem;
                    }

                    .info-label {
                        font-size: 0.75rem;
                        margin-bottom: 0.25rem;
                    }

                    .info-value {
                        font-size: 0.85rem;
                    }

                    .info-value .badge {
                        font-size: 0.75rem;
                        padding: 0.25rem 0.5rem;
                    }

                    .action-card {
                        padding: 0.5rem;
                    }

                    .action-card-header {
                        margin-bottom: 0.375rem;
                    }

                    .action-card-header i {
                        font-size: 1rem;
                    }

                    .action-card-header h5 {
                        font-size: 0.85rem;
                    }

                    .action-card-body p {
                        font-size: 0.75rem;
                        margin-bottom: 0.5rem;
                    }
                }
            `}</style>
        </>
    )
}

export default PaymentReceiveDetails 